/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Render the search overlay with its filters, recents and empty state.
 * @state-owner overlay-local React state
 * @allowed-imports React; workspace store; shared demo dataset and hooks; role provider
 * @forbidden direct API client; module internals
 * @public-api WorkspaceShell
 * @side-effects navigation
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useEffect, useRef, useState } from 'react';
import { useActiveRole } from '../../app/providers/ActiveRoleProvider';
import { SearchIcon } from '../../shared/components/VispoIcon';
import { SEARCH_ROWS_STUDENT, SEARCH_ROWS_TEACHER } from '../../shared/lib/demo-data';
import type { WorkspaceSection } from '../../shared/lib/model';
import { defaultSelection } from '../../shared/lib/selectors';
import { useDatasetStore } from '../../shared/model/dataset-store';
import { useTranslator } from '../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';

const CHIP_COLORS: Readonly<Record<string, readonly [string, string]>> = {
  課程: ['var(--primary-pale)', 'var(--primary)'],
  活動: ['rgba(16,185,129,.14)', '#047857'],
  學生: ['var(--sc)', 'var(--on-surface-variant)'],
  對話: ['var(--error-container)', 'var(--error)'],
  資源: ['var(--warn-pale)', 'var(--warn)'],
};

export function SearchOverlay({ onClose, open }: { onClose(): void; open: boolean }) {
  const t = useTranslator();
  const role = useActiveRole();
  const teacher = role === 'teacher';
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('全部');
  const inputRef = useRef<HTMLInputElement>(null);
  const go = useWorkspaceUiStore((state) => state.go);
  const classes = useDatasetStore((state) => state.classes);
  const runs = useDatasetStore((state) => state.runs);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    const timeout = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(timeout);
  }, [open]);

  const filters = teacher ? ['全部', '活動', '學生'] : ['全部', '活動'];
  const source = teacher ? SEARCH_ROWS_TEACHER : SEARCH_ROWS_STUDENT;
  const trimmed = query.trim();
  const rows = source
    .filter((row) => filter === '全部' || row[0] === filter)
    .filter((row) => !trimmed || row[1].includes(trimmed) || row[2].includes(trimmed));

  const openRow = (section: string) => {
    onClose();
    const target = section as WorkspaceSection;
    go(target, defaultSelection(target, role, classes, runs));
  };

  const renderRow = (row: readonly [string, string, string, string]) => {
    const colors = CHIP_COLORS[row[0]] ?? CHIP_COLORS.活動;
    return (
      <button className="sr" key={row[1]} type="button" onClick={() => openRow(row[3])}>
        <span
          className="sq2 sq"
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--r)',
            display: 'grid',
            placeItems: 'center',
            background: colors[0],
            color: colors[1],
            fontSize: 13,
            fontWeight: 700,
            flex: '0 0 32px',
          }}
        >
          {row[0][0]}
        </span>
        <span className="t">
          <b>{row[1]}</b>
          <span>{row[2]}</span>
        </span>
        <em>{row[0]}</em>
      </button>
    );
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the scrim is a shortcut; Escape and the panel controls are the accessible paths
    <div
      className={`sov${open ? ' on' : ''}`}
      id="sov"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div className="spanel">
        <div className="sbar">
          <SearchIcon />
          <input
            id="sq"
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`${t('輸入指令或搜尋')}…`}
            ref={inputRef}
            value={query}
          />
          <span className="esc">esc</span>
        </div>
        <div className="sfil" id="sfil">
          {filters.map((item) => (
            <button
              className={filter === item ? 'on' : ''}
              key={item}
              type="button"
              onClick={() => setFilter(item)}
            >
              {t(item)}
            </button>
          ))}
        </div>
        <div className="sres" id="sres">
          {!trimmed && filter === '全部' ? (
            <>
              <div className="sgrp">{t('最近開過的')}</div>
              {source.slice(0, 4).map(renderRow)}
              <div className="sempty" style={{ padding: '34px 20px 40px' }}>
                <div className="glyphs">
                  {Array.from({ length: 15 }, (_, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: decorative glyph grid
                    <i key={index} />
                  ))}
                </div>
                <b>{t('搜尋 Vispo')}</b>
                <p>{teacher ? t('活動與學生都在這裡找得到') : t('你的作業都在這裡找得到')}</p>
              </div>
            </>
          ) : rows.length ? (
            rows.map(renderRow)
          ) : (
            <div className="sempty">
              <b>{`找不到「${trimmed}」`}</b>
              <p>{t('換個關鍵字，或改用上面的分類看看。')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
